import { useEffect, useRef, useState, type ReactNode } from "react";

import { useLocale, useT, type Locale } from "../../shared/i18n";
import {
  Badge,
  Button,
  Card,
  GroupLabel,
  LargeTitle,
  ListRow,
} from "../../shared/ui";
import type { BillingOptions } from "../../shared/api/client";
import { ProfileSettings } from "./ProfileSettings";
import { DataPrivacy, type DataPrivacyApi } from "./DataPrivacy";
import { SbpUpgrade } from "./SbpUpgrade";

export interface ProfileSnapshot {
  plan_id: string | null;
  subscription_state: string;
  billing_enabled: boolean;
  usage: Record<string, number>;
  packs: readonly { tier: string; remaining_credits: number }[];
  orders: readonly { product_id: string; state: string }[];
  is_operator?: boolean;
  locale?: string | null;
  timezone?: string | null;
}

/** Rows carry their own hairline; the last one in a card drops it. */
const ROWS = "[&>li:last-child>[data-row]]:after:hidden";

/** What the upgrade button is doing, from the person's point of view. */
type UpgradeState = "idle" | "opening" | "confirming" | "failed";
const CONFIRMATION_TIMEOUT_MS = 90_000;

function stateTone(state: string) {
  if (state === "active" || state === "paid") return "success" as const;
  if (state === "pending") return "info" as const;
  if (state === "failed" || state === "refunded") return "error" as const;
  return "neutral" as const;
}

export function Profile({
  snapshot,
  onUpgrade,
  onTimezone,
  onLocale,
  operatorEntry,
  billingOptions,
  onPaySbp,
  onCheckOrder,
  onRefresh,
  accountApi,
  onAccountDeleted,
}: {
  snapshot: ProfileSnapshot;
  /**
   * Opens the invoice and resolves once the backend has been asked again.
   * It never reports success on its own: Telegram closing the sheet says
   * the person paid, not that we recorded it.
   */
  onUpgrade?: (idempotencyKey: string) => Promise<void>;
  onTimezone?: (zone: string) => Promise<void>;
  onLocale?: (locale: Locale) => Promise<void>;
  /** Operator console entry; shown in the service group for operators only. */
  operatorEntry?: ReactNode;
  /** Enabled channels and catalog; the SBP entry exists only when it lists "sbp". */
  billingOptions?: BillingOptions | null;
  /** Opens the SBP provider link for a product and resolves once handed over. */
  onPaySbp?: (
    idempotencyKey: string,
    productId: string,
  ) => Promise<string | null>;
  /** State of the order opened by `onPaySbp`, for the confirmation poll. */
  onCheckOrder?: (orderId: string) => Promise<string>;
  /** Fetches the profile again so a settled payment can be observed. */
  onRefresh?: () => Promise<void>;
  /** Export, clear and delete controls; absent where there is no account. */
  accountApi?: DataPrivacyApi;
  /** The owner acknowledged a scheduled deletion. */
  onAccountDeleted?: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [upgrade, setUpgrade] = useState<UpgradeState>("idle");
  const checkoutKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (upgrade !== "confirming") return;
    const timer = window.setTimeout(() => {
      setUpgrade("idle");
    }, CONFIRMATION_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [upgrade]);
  const sellable =
    snapshot.billing_enabled && snapshot.plan_id !== "pro" && onUpgrade;
  const start = async () => {
    if (upgrade === "opening" || upgrade === "confirming") return;
    setUpgrade("opening");
    try {
      checkoutKey.current ??= crypto.randomUUID();
      await onUpgrade?.(checkoutKey.current);
      setUpgrade("confirming");
    } catch (error) {
      console.error("Pro checkout failed", error);
      setUpgrade("failed");
    }
  };
  return (
    <main
      aria-label={t("profile.title")}
      className="mx-auto flex w-full max-w-5xl flex-col gap-4 pb-6"
    >
      <LargeTitle title={t("profile.title")} className="pb-1 pt-4" />

      {onTimezone && onLocale && (
        <ProfileSettings
          timezone={snapshot.timezone}
          locale={locale}
          onTimezone={onTimezone}
          onLocale={onLocale}
        />
      )}

      <Card title={t("profile.plan")}>
        <div className="flex items-center gap-2">
          <strong className="text-h3 text-fg">
            {snapshot.plan_id ?? "Starter"}
          </strong>
          <Badge tone={stateTone(snapshot.subscription_state)}>
            {snapshot.subscription_state}
          </Badge>
        </div>
        {sellable ? (
          <div className="mt-3 flex flex-col gap-2">
            <Button
              disabled={upgrade === "opening" || upgrade === "confirming"}
              onClick={() => {
                void start();
              }}
            >
              {t("profile.upgrade")}
            </Button>
            {upgrade !== "idle" ? (
              <p className="text-caption text-fg-muted">
                {t(
                  upgrade === "opening"
                    ? "profile.upgradePending"
                    : upgrade === "confirming"
                      ? "profile.upgradeChecking"
                      : "profile.upgradeFailed",
                )}
              </p>
            ) : null}
          </div>
        ) : null}
        {snapshot.billing_enabled && onPaySbp ? (
          <SbpUpgrade
            options={billingOptions}
            planId={snapshot.plan_id}
            entitlementSignature={`${snapshot.plan_id ?? ""}|${String(
              snapshot.packs.reduce((sum, p) => sum + p.remaining_credits, 0),
            )}`}
            onPay={onPaySbp}
            onCheckOrder={onCheckOrder}
            onRefresh={onRefresh}
          />
        ) : null}
        {!snapshot.billing_enabled ? (
          <p className="mt-3 text-caption text-fg-muted">
            {t("profile.billingDisabled")}
          </p>
        ) : null}
      </Card>

      <Card flush title={t("profile.quotas")}>
        {Object.keys(snapshot.usage).length === 0 ? (
          <p className="px-4 pb-4 text-body text-fg-muted">—</p>
        ) : null}
        <ul className={ROWS}>
          {Object.entries(snapshot.usage).map(([key, value]) => (
            <li key={key}>
              <ListRow
                kind="setting"
                title={key === "committed" ? t("profile.usageCommitted") : key}
                trailing={<span>{value}</span>}
              />
            </li>
          ))}
        </ul>
      </Card>

      <Card flush title={t("profile.packs")}>
        {snapshot.packs.length === 0 ? (
          <p className="px-4 pb-4 text-body text-fg-muted">
            {t("profile.noPacks")}
          </p>
        ) : null}
        <ul className={ROWS}>
          {snapshot.packs.map((pack, index) => (
            <li key={`${pack.tier}-${String(index)}`}>
              <ListRow
                kind="setting"
                title={pack.tier}
                trailing={<span>{pack.remaining_credits}</span>}
              />
            </li>
          ))}
        </ul>
      </Card>

      <Card flush title={t("profile.orders")}>
        {/* Success is only ever shown for a server-stored, confirmed payment
            event — an opened invoice is not a completed payment. */}
        {snapshot.orders.length === 0 ? (
          <p className="px-4 pb-2 text-body text-fg-muted">
            {t("profile.noOrders")}
          </p>
        ) : null}
        <ul className={ROWS}>
          {snapshot.orders.map((order, index) => (
            <li key={`${order.product_id}-${String(index)}`}>
              <ListRow
                kind="setting"
                title={order.product_id}
                trailing={
                  <Badge tone={stateTone(order.state)}>{order.state}</Badge>
                }
              />
            </li>
          ))}
        </ul>
        <p className="px-4 pb-4 pt-2 text-caption text-fg-muted">
          {t("profile.invoiceNote")}
        </p>
      </Card>

      {accountApi && onAccountDeleted ? (
        <DataPrivacy api={accountApi} onDeleted={onAccountDeleted} />
      ) : null}

      {snapshot.is_operator === true && operatorEntry ? (
        <section
          aria-label={t("profile.service")}
          className="flex flex-col gap-2"
        >
          <GroupLabel>{t("profile.service")}</GroupLabel>
          <Card>{operatorEntry}</Card>
        </section>
      ) : null}
    </main>
  );
}
